import { Component, ViewEncapsulation, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { Integrations } from 'src/providers/integrations/integrations';
import { AddCustomFieldPage } from '../../integrations/integration-settings/add-custom-field/add-custom-field';

@Component({
  templateUrl: 'custom-fields.html',
  styleUrls: ['custom-fields.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})

export class CustomFieldsPage implements OnInit, OnDestroy {

  customFields: any[] = [];
  currentRows: any[] = [];
  form: UntypedFormGroup;
  headers = ['Field Name', 'Model Type', 'Field Type', 'Visibility', 'Actions'];
  loaded = false;
  isDataLoaded = false;
  errorMessage = '';
  isRightSideContent = true;
  instance: any = null;

  modelTypeFilter = [
    { value: '', viewValue: 'All Model Types' },
    { value: 'Address', viewValue: 'Property' },
    { value: 'GuestReservation', viewValue: 'Reservation' },
    { value: 'Job', viewValue: 'Job' },
    { value: 'Task', viewValue: 'Task' },
    { value: 'Pro', viewValue: 'Pro' },
    { value: 'Item', viewValue: 'Items' },
  ];

  @ViewChild('tidyTable') tidyTable: any;
  panelClosedSubscription: Subscription;

  constructor(
    private navCtrl: CustomNavController,
    private fb: UntypedFormBuilder,
    public windowService: WindowService,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    private util: Util,
    private integrations: Integrations,
  ) {
    this.form = this.fb.group({
      selectedModelType: [''],
      search: ['']
    });
  }

  async ngOnInit() {
    try {
      this.rightSidePanelService.setDialogPageTitle('Custom Fields');
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      console.log('isRightSideContent', this.isRightSideContent);
      this.loaded = true;
      await this.getPageData();
    } catch (err) {
      console.error(err);
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  ngOnDestroy() {
    this.panelClosedSubscription?.unsubscribe();
  }

  async getPageData() {
    try {
      this.isDataLoaded = false;
      const dialogParams = await this.storage.retrieve('dialog-params');
      const modelType = dialogParams?.modelType || '';
      this.instance = dialogParams?.instance;
      this.customFields = await this.integrations.getCustomFields(modelType);
      this.buildRows();
      this.isDataLoaded = true;
      console.log('isDataLoaded', this.isDataLoaded);
      console.log('customFields', this.customFields);
      console.log('currentRows', this.currentRows);
    } catch (err) {
      console.error(err);
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  buildRows() {
    this.currentRows = this.customFields
      .filter(field => this.matchesFilters(field))
      .map(field => ({
        id: field.id,
        'Field Name': {
          value: field.field_name,
          action: this.editCustomField.bind(this, field),
        },
        'Model Type': {
          value: this.parseModelType(field.model_type),
          action: null,
        },
        'Field Type': {
          value: this.getFieldTypeDisplay(field.field_type),
          action: null,
        },
        'Visibility': {
          value: field.is_visible_to_homekeeper ? 'Public' : 'Private',
          action: null,
        },
        'Actions': {
          value: '',
          action: this.editCustomField.bind(this, field),
          icon: 'assets/ionic-icon-v3/ios-create-outline.svg',
        }
      }));
      console.log('currentRows', this.currentRows);
  }

  parseModelType(modelType: string): string {
    const modelTypeMap = {
      'Address': 'Property',
      'GuestReservation': 'Reservation',
      'Booking': 'Job',
      'IssueReport': 'Task',
      'RoomObject': 'Item',
    };
    return modelTypeMap[modelType] || modelType;
  }

  matchesFilters(field: any): boolean {
    const modelTypeFilter = this.form.get('selectedModelType')?.value;
    console.log('modelTypeFilter', modelTypeFilter);
    const searchTerm = this.form.get('search')?.value?.toLowerCase() || '';

    console.log('field.model_type', field.model_type);

    const matchesModelType = !modelTypeFilter || field.model_type === modelTypeFilter;
    const matchesSearch = !searchTerm || field.field_name.toLowerCase().includes(searchTerm);

    return matchesModelType && matchesSearch;
  }

  getFieldTypeDisplay(fieldType: string): string {
    const typeMap = {
      'string': 'Text',
      'integer': 'Number',
      'boolean': 'Boolean'
    };
    return typeMap[fieldType] || fieldType;
  }

  onFilterValueChange() {
    this.buildRows();
  }

  onSearchChange() {
    // Debounce search if needed
    this.buildRows();
  }

  async addCustomField() {
    const params = {
      navBackTo: 'more/custom-fields'
    };

    if (this.windowService.isDesktopRes) {
      await this.storage.save('dialog-right-side-open', true);
      await this.storage.save('dialog-params', params);
      this.rightSidePanelService.navigateTo('integration/add-custom-field', params, AddCustomFieldPage);

      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().subscribe(async () => {
        await this.getPageData(); // Refresh data when panel closes
      });
    } else {
      await this.storage.save('dialog-right-side-open', false);
      this.navCtrl.navigateForward('integration/add-custom-field', params);
    }
  }

  async editCustomField(field: any) {
    const params = {
      customField: field,
      instance: this.instance,
      navBackTo: 'more/custom-fields'
    };

    await this.storage.save('dialog-params', params);
    if (this.windowService.isDesktopRes) {
      await this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.navigateTo('integration/add-custom-field', params, AddCustomFieldPage);

      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().subscribe(async () => {
        await this.getPageData(); // Refresh data when panel closes
      });
    } else {
      await this.storage.save('dialog-right-side-open', false);
      this.navCtrl.navigateForward('integration/add-custom-field', params);
    }
  }

  onTableColumnClick(event: any) {
    // Handle table row clicks if needed
    if (event && event.id) {
      const field = this.customFields.find(f => f.id === event.id);
      if (field) {
        this.editCustomField(field);
      }
    }
  }

  goBack() {
    this.navCtrl.navigateBack('/more');
  }
}
